import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../service/api";

// Fetch user agreement
export const getUserAgreement = createAsyncThunk(
  "serviceAgreement/getUserAgreement",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("api/users/get/user-agreement");
      return data.agreement;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Agree to service agreement
export const agreeUserAgreement = createAsyncThunk(
  "client/agreeUserAgreement",
  async (userId, { rejectWithValue }) => {
    try {
      const { data } = await api.put("api/users/put/user-agreement");
      return data.agreement;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Get all users
export const listUsers = createAsyncThunk(
  "users/listUsers",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("api/users");
      return data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error.message ||
          "Failed to fetch users"
      );
    }
  }
);

// Register new user (admin)
export const userRegister = createAsyncThunk(
  "users/registerUser",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post("api/users/register", payload);
      return data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error.message ||
          "User registration failed"
      );
    }
  }
);
// Get single user
export const getSingleUser = createAsyncThunk(
  "client/getSingleUser",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`api/users/client/${id}`);
      return data.user;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to load user"
      );
    }
  }
);

// Update user settings
export const updateUserSettings = createAsyncThunk(
  "client/updateUserSettings",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.put("api/users/settings", payload);

      if (data?.success) {
        return data;
      } else {
        return rejectWithValue(
          data?.error || "Settings update failed (no success flag)"
        );
      }
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Settings update failed"
      );
    }
  }
);

export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async (userId, { rejectWithValue }) => {
    try {
      await api.delete(`api/users/${userId}`);
      return userId;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error.message ||
          "Failed to delete user"
      );
    }
  }
);

// Get BotRef Details
// ------------------------------
export const getBotRefDetails = createAsyncThunk(
  "client/getBotRefDetails",
  async (botRef, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/api/botrefs/${botRef}`);
      return data.botRef;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to load BotRef details"
      );
    }
  }
);

// Update BotRef URL

export const updateBotRefUrl = createAsyncThunk(
  "client/updateBotRefUrl",
  async ({ botRef, newUrl }, { rejectWithValue }) => {
    try {
      const { data } = await api.put("/api/botRefs/update-url", {
        botRef,
        newUrl,
      });
      return data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update BotRef URL"
      );
    }
  }
);

const initialState = {
  list: [],
  partners: [],
  clients: [],

  selectedUser: null,
  loading: false,
  error: null,
  success: false,

  listLoading: false,
  listError: null,

  registerLoading: false,
  registerError: null,
  registerSuccess: false,
  createdUser: null,

  deleteLoading: false,
  deleteError: null,
  deleteSuccess: false,

  settingsLoading: false,
  settingsError: null,
  settingsSuccess: false,

  botRefDetails: null,
  botRefDetailsLoading: false,
  botRefDetailsError: null,

  botRefUpdateLoading: false,
  botRefUpdateSuccess: false,
  botRefUpdateError: null,
};

const clientSlice = createSlice({
  name: "client",
  initialState,
  reducers: {
    clearClientError(state) {
      state.error = null;
      state.settingsError = null;
    },
    clearSettingsResult(state) {
      console.log("clearSettingsResult function was called");
      state.settingsLoading = false;
      state.settingsError = null;
      state.settingsSuccess = false;
    },
    clearRegisterResult(state) {
      state.registerLoading = false;
      state.registerError = null;
      state.registerSuccess = false;
      state.createdUser = null;
    },
    clearDeleteResult(state) {
      state.deleteLoading = false;
      state.deleteError = null;
      state.deleteSuccess = false;
    },
    clearBotRefDetails(state) {
      state.botRefDetails = null;
      state.botRefDetailsLoading = false;
      state.botRefDetailsError = null;
    },

    resetBotRefUpdate(state) {
      state.botRefUpdateLoading = false;
      state.botRefUpdateSuccess = false;
      state.botRefUpdateError = null;
    },
  },
  extraReducers: (builder) => {
    // listUsers
    builder
      .addCase(listUsers.pending, (state) => {
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(listUsers.fulfilled, (state, action) => {
        state.listLoading = false;
        state.list = action.payload?.users || action.payload || [];

        //  Categorize
        state.partners = state.list.filter((u) => u.isPartner);
        state.clients = state.list.filter(
          (u) => !u.isSuperAdmin && !u.isPartner && !u.isAdmin
        );
      })
      .addCase(listUsers.rejected, (state, action) => {
        state.listLoading = false;
        state.listError = action.payload;
      });

    // getSingleUser
    builder
      .addCase(getSingleUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedUser = null;
      })
      .addCase(getSingleUser.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUser = action.payload;
        state.success = true;
        state.error = null;
      })
      .addCase(getSingleUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });

    // userRegister
    builder
      .addCase(userRegister.pending, (state) => {
        state.registerLoading = true;
        state.registerError = null;
        state.registerSuccess = false;
      })
      .addCase(userRegister.fulfilled, (state, action) => {
        state.registerLoading = false;
        state.registerSuccess = true;
        state.createdUser = action.payload;
      })
      .addCase(userRegister.rejected, (state, action) => {
        state.registerLoading = false;
        state.registerError = action.payload;
        state.registerSuccess = false;
      });

    builder
      // deleteUser
      .addCase(deleteUser.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
        state.deleteSuccess = false;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.deleteSuccess = true;
        state.list = state.list.filter((u) => u._id !== action.payload);
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload;
      });

    // updateUserSettings
    builder
      .addCase(updateUserSettings.pending, (state) => {
        state.settingsLoading = true;
        state.settingsError = null;
      })
      .addCase(updateUserSettings.fulfilled, (state, action) => {
        state.settingsLoading = false;
        state.settingsError = null;
        state.settingsSuccess = true;
        state.selectedUser = action.payload.user;
      })
      .addCase(updateUserSettings.rejected, (state, action) => {
        state.settingsLoading = false;
        state.settingsError = action.payload;
      });

    // getBotRefDetails

    builder
      .addCase(getBotRefDetails.pending, (state) => {
        state.botRefDetailsLoading = true;
        state.botRefDetailsError = null;
        state.botRefDetails = null;
      })
      .addCase(getBotRefDetails.fulfilled, (state, action) => {
        state.botRefDetailsLoading = false;
        state.botRefDetails = action.payload;
        state.botRefDetailsError = null;
      })
      .addCase(getBotRefDetails.rejected, (state, action) => {
        state.botRefDetailsLoading = false;
        state.botRefDetailsError = action.payload;
      });

    // updateBotRefUrl

    builder
      .addCase(updateBotRefUrl.pending, (state) => {
        state.botRefUpdateLoading = true;
        state.botRefUpdateError = null;
        state.botRefUpdateSuccess = false;
      })
      .addCase(updateBotRefUrl.fulfilled, (state) => {
        state.botRefUpdateLoading = false;
        state.botRefUpdateSuccess = true;
        state.botRefUpdateError = null;
      })
      .addCase(updateBotRefUrl.rejected, (state, action) => {
        state.botRefUpdateLoading = false;
        state.botRefUpdateError = action.payload;
      });
  },
});

export const {
  clearClientError,
  clearSettingsResult,
  clearRegisterResult,
  clearDeleteResult,
  clearBotRefDetails,
  resetBotRefUpdate,
} = clientSlice.actions;
export default clientSlice.reducer;
